import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryRetailService from '../../services/nurseryRetail';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz, getTodayInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';
import { XDOCK_DAY_NAMES, type XdockPickAnswer, type XdockSchedule, type XdockScheduleLine } from '../../types/nurseryRetail';

/**
 * /nursery/cross-dock/:id — one schedule: the stores on each pick day, and a
 * tester for the lookup a purchase order gets.
 *
 * The tester asks every schedule in use, not only this one, so its answer is
 * the one an import would give for that store and date.
 */

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';

function CrossDockScheduleDetail() {
  const { id } = useParams<{ id: string }>();
  const tz = useOrgTimezone();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [schedule, setSchedule] = useState<XdockSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oursOnly, setOursOnly] = useState(false);

  const [store, setStore] = useState('');
  const [date, setDate] = useState(() => getTodayInTz(tz));
  const [asked, setAsked] = useState<{ store: string; date: string } | null>(null);
  const [answer, setAnswer] = useState<XdockPickAnswer | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryRetailService.getSchedule(Number(id));
      if (res.success) setSchedule(res.data);
      else setError(errorMessage(res, 'Could not load the schedule'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the schedule'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const byDay = useMemo(() => {
    const days: XdockScheduleLine[][] = XDOCK_DAY_NAMES.map(() => []);
    for (const line of schedule?.lines ?? []) {
      if (oursOnly && !line.customer_name) continue;
      days[line.day_of_week]?.push(line);
    }
    return days;
  }, [schedule, oursOnly]);

  const ours = useMemo(
    () => new Set((schedule?.lines ?? []).filter((l) => l.customer_name).map((l) => l.store_code)).size,
    [schedule],
  );

  const test = async () => {
    const code = store.trim();
    if (!code || !date) return;
    setTesting(true);
    setTestError(null);
    setAnswer(null);
    try {
      const res = await nurseryRetailService.lookup(code, date);
      if (res.success) {
        setAnswer(res.data);
        setAsked({ store: code, date });
      } else setTestError(errorMessage(res, 'Could not check that store'));
    } catch (e) {
      setTestError(errorMessage(e, 'Could not check that store'));
    } finally {
      setTesting(false);
    }
  };

  if (licenseLoading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what="Cross dock schedules are kept" />;

  const applies = !schedule
    ? ''
    : schedule.is_default || !schedule.commence_date
      ? 'Standing schedule: any date no dated schedule covers'
      : `${formatLocalDateInTz(schedule.commence_date, tz)} to ${schedule.end_date ? formatLocalDateInTz(schedule.end_date, tz) : ''}`;

  return (
    <>
      <PageHeader
        title={schedule?.name || 'Cross dock schedule'}
        subtitle={applies || undefined}
        icon={<CalendarClock className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : schedule && (
          <>
            {!schedule.active && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
                This schedule has been removed and no longer decides any pick day. It is kept because orders were placed by it.
              </div>
            )}

            <p className="text-xs text-neutral-500 break-words">
              {schedule.store_count} stores, {ours} of them customers of yours · uploaded {formatDateInTz(schedule.created_at, tz)}
              {schedule.created_by_name ? ` by ${schedule.created_by_name}` : ''}
              {schedule.updated_note ? ` · sheet ${schedule.updated_note}` : ''}
              {schedule.source_file ? ` · ${schedule.source_file}` : ''}
            </p>

            <section aria-label="Check a store" className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold text-neutral-800">Check a store</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:items-end">
                <div>
                  <label htmlFor="xdock-test-store" className="block text-sm font-medium text-neutral-700 mb-1">Store number</label>
                  <input id="xdock-test-store" className={inputCls} inputMode="numeric" maxLength={20} value={store}
                    onChange={(e) => setStore(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') test(); }} />
                </div>
                <div>
                  <label htmlFor="xdock-test-date" className="block text-sm font-medium text-neutral-700 mb-1">Date the order asks for</label>
                  <input id="xdock-test-date" type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div>
                  <Button size="sm" onClick={test} disabled={testing || !store.trim() || !date}>
                    {testing ? 'Checking…' : 'Check'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-neutral-500">
                This asks every schedule in use, not only this one, so the answer is what a purchase order for that store and date gets.
              </p>

              {testError && <p className="text-sm text-red-700 break-words">{testError}</p>}
              {answer && asked && (answer.ok ? (
                <div role="status" className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900 space-y-1">
                  <p className="font-semibold break-words">
                    Store {asked.store} is picked on {answer.day_name} {formatLocalDateInTz(answer.date, tz)}
                  </p>
                  <p className="break-words">
                    {[answer.route ? `Route ${answer.route}` : null, answer.del_seq ? `delivery ${answer.del_seq}` : null,
                      `from ${answer.schedule_name}`].filter(Boolean).join(' · ')}
                  </p>
                  <p className="break-words">
                    {answer.moved_days > 0
                      ? `Moved forward ${answer.moved_days} day${answer.moved_days === 1 ? '' : 's'} from ${formatLocalDateInTz(asked.date, tz)}: the store is not picked that day.`
                      : 'The day asked for is already a pick day for this store.'}
                  </p>
                </div>
              ) : (
                <div role="status" className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 break-words">
                  {answer.message} A purchase order for it keeps the date it asks for and is not put on a load.
                </div>
              ))}
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-neutral-800">Stores by pick day</h2>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="checkbox" className="h-4 w-4" checked={oursOnly} onChange={(e) => setOursOnly(e.target.checked)} />
                  Only stores we supply
                </label>
              </div>

              {schedule.lines && schedule.lines.length === 0 && (
                <p className="text-sm text-red-700">This schedule has no store rows.</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {XDOCK_DAY_NAMES.map((day, d) => {
                  const lines = byDay[d];
                  // A day with no column on the sheet (Saturday) is left out
                  // unless the filter emptied it.
                  if (!lines.length && !(schedule.lines ?? []).some((l) => l.day_of_week === d)) return null;
                  return (
                    <div key={day} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                      <div className="bg-neutral-50 px-3 py-2 flex justify-between gap-2 text-sm">
                        <span className="font-semibold text-neutral-800">{day}</span>
                        <span className="text-neutral-500">{lines.length} store{lines.length === 1 ? '' : 's'}</span>
                      </div>
                      {lines.length === 0 ? (
                        <p className="px-3 py-3 text-sm text-neutral-400">None of your customers.</p>
                      ) : (
                        <ul className="divide-y divide-neutral-100 max-h-96 overflow-y-auto">
                          {lines.map((l, i) => (
                            <li key={`${l.store_code}-${i}`} className="px-3 py-1.5 flex items-start gap-2 text-sm">
                              <span className="w-12 flex-shrink-0 font-mono text-xs text-neutral-500 pt-0.5">{l.store_code}</span>
                              <span className="min-w-0 flex-1">
                                <span className={cn('block break-words', l.customer_name ? 'text-neutral-900 font-medium' : 'text-neutral-600')}>
                                  {l.store_name || 'No name'}
                                </span>
                                {l.customer_name && l.customer_name !== l.store_name && (
                                  <span className="block text-xs text-green-700 break-words">{l.customer_name}</span>
                                )}
                              </span>
                              <span className="flex-shrink-0 text-right text-xs text-neutral-600 max-w-[45%]">
                                <span className="block break-words">{l.route || ''}</span>
                                {l.del_seq && <span className="block text-neutral-400">Del. {l.del_seq}</span>}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}

export default CrossDockScheduleDetail;
